import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import moment from "moment";
import axiosInstance from "../config/axios";
import { API_URLS, domain } from "../config/APIUrls";
import CustomToPagination from "../Shared/CustomToPagination";
import CustomFilter from "../Shared/CustomForFiler";
import CustomMap from "../Shared/CustomMap";
import { LocationOn } from "@mui/icons-material";
import { Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useLocation } from "react-router-dom";

const Attendance = () => {
    const formatDate = (d) => d.toISOString().split("T")[0];
    const location = useLocation();
    const queryClient = useQueryClient();
    const today = formatDate(new Date());
    const [page, setPage] = useState(1);
    const [zoomImg, setZoomImg] = useState(null);
    const [mapData, setMapData] = useState(null);
    const zoneId = location.state?.zone_id;

    useEffect(() => {
        if (zoneId) {
            setFilters((prev) => ({
                ...prev,
                zone: zoneId,
            }));
            setPage(1);
        }
    }, [zoneId]);


    useEffect(() => {
        if (location.state) {
            setFilters((prev) => ({
                ...prev,
                zone: location.state.zone_id || "",
                status: location.state.status || "",
            }));
            setPage(1);
        }
    }, [location.state]);


    const [filters, setFilters] = useState({
        zone: "",
        village: "",
        start_date: today,
        end_date: today,
        search: "",
        status: "",

    });

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setPage(1);
        queryClient.invalidateQueries(["get_attendance", page, filters]);
    };

    const clearFilters = () => {
        setFilters({
            zone: "",
            village: "",
            start_date: "",
            end_date: "",
            search: "",
            status: "",

        });
        setPage(1);
        queryClient.invalidateQueries(["get_attendance"]);
    };

    const { data: attData, isLoading: loading } = useQuery(
        ["get_attendance", page, filters],
        () =>
            axiosInstance.get(API_URLS.get_attendance_report, {
                params: { ...filters, page, count: 10 },
            }),
        { refetchOnWindowFocus: true }
    );

    const attendanceList = attData?.data?.response || [];

    const { data: zoneData } = useQuery(
        "get_zone_master",
        () =>
            axiosInstance.get(API_URLS.get_zone_master, {
                params: { count: 1000000, status: "1" },
            }),
        { refetchOnWindowFocus: true }
    );

    const zoneOptions = zoneData?.data?.response?.data || [];

    const { data: villageData } = useQuery(
        ["get_village_master", filters.zone],
        () =>
            axiosInstance.get(API_URLS.get_village_master, {
                params: { count: 1000000, status: "1" },
            }),
        { refetchOnWindowFocus: true }
    );

    const villageOptions = villageData?.data?.response?.data || [];

   const handleExport = async () => {
            try {
                const res = await axiosInstance.get(
                    API_URLS.excel_attendance_report,
                    {
                        params: {
                            ...filters  
                        }
                    }
                );

                const fileUrl = res?.data?.response?.url;
                if (!fileUrl) {
                    toast.error("No file URL found");
                    return;
                }
    
                window.open(fileUrl, "_blank");
            } catch (e) {
                console.error(e);
                toast.error("Something went wrong");
            }
        };

    const { data: totalData } = useQuery(
        ["get_attendance_total", filters],
        () =>
            axiosInstance.get(API_URLS.get_attendance_report, {
                params: { ...filters, page: 1, count: 1000000 },
            }),
        { refetchOnWindowFocus: false }
    );

    const summaryCounts = totalData?.data?.response?.data?.reduce(
        (acc, item) => {
            if (item.e03_final_status === "HD") acc.HD += 1;
            else if (item.e03_final_status === "L") acc.L += 1;
            else if (item.e03_final_status === "A") acc.A += 1;
            else if (item.e03_final_status === "P") acc.P += 1;
            else if (item.e03_final_status === "H") acc.H += 1;
            else if (item.e03_final_status === "WO") acc.WO += 1;
            else if (item.e03_final_status === "M") acc.M += 1;
            return acc;
        },
        { HD: 0, L: 0, A: 0, P: 0, H: 0, WO: 0, M: 0 }
    ) || { HD: 0, L: 0, A: 0, P: 0, H: 0, WO: 0, M: 0 };


    const statusOptions = [
        { label: "Absent", value: "A" },
        { label: "Present", value: "P" },
        { label: "Half Day", value: "HD" },
        { label: "Leave", value: "L" },
        { label: "Week OFF", value: "WO" },
        { label: "Missing", value: "M" },
        { label: "Holiday", value: "H" },
    ];

    const statusColors = {
        HD: "#FEF3C7",
        L: "#DBEAFE",
        A: "#FEE2E2",
        P: "#D1FAE5",
        WO: "#E5E7EB",
        M: "#FED7AA",
        H: "#E9D5FF",
    };

    const tableHead = [
        "S.No", "Block", "Village",
        "Name", "Mobile No.", "Date/Time IN", "Date/Time OUT",
        "Profile IN", "Profile OUT", "Status IN", "Status OUT", "Final Status"
    ];

    const tableRows = attendanceList?.data?.map((item, index) => {
        const rowColor = statusColors[item.e03_final_status] || "";
        return [
            <div className={rowColor + " flex justify-between"}>
                {index + 1} </div>,
            <div className={rowColor}>{item.m05_zn_name || "--"}</div>,
            <div className={rowColor}>{item.m06_vi_name || "--"}</div>,
            <div className={rowColor}>{item.e02_f_name || "--"}</div>,
            <div className={rowColor}>{item.e01_mob || "--"}</div>,
            <div className={rowColor}>{item.e03_att_datetime_in ? moment?.utc(item.e03_att_datetime_in)?.format("HH:mm:ss DD-MM-YYYY") : "--"}</div>,
            <div className={rowColor}> {item.e03_att_datetime_out ? moment?.utc(item.e03_att_datetime_out)?.format("HH:mm:ss DD-MM-YYYY") : "--"}</div>,
            <div className={rowColor + "flex flex-col items-center"}>
                <img
                    src={domain + item.e03_att_img_in}
                    alt="not found"
                    className="h-5 w-5 cursor-pointer !rounded-full"
                    onClick={() => setZoomImg(domain + item.e03_att_img_in)}
                />
                {item.e03_att_lat_in && (
                    <LocationOn
                        style={{ color: "red", fontSize: "20px", cursor: "pointer", marginTop: "4px" }}
                        onClick={() => {
                            const locs = [];
                            if (item.e03_att_lat_in) {
                                locs.push({
                                    lat: Number(item.e03_att_lat_in),
                                    lng: Number(item.e03_att_lng_in),
                                    address: item.e03_att_locat_in,
                                    img: domain + item.e03_att_img_in,
                                    radius: item.m06_vi_radius,
                                    type: "IN"
                                });
                            }
                            if (item.e03_att_lat_out && item.e03_att_lat_out !== "0.00000") {
                                locs.push({
                                    lat: Number(item.e03_att_lat_out),
                                    lng: Number(item.e03_att_lng_out),
                                    address: item.e03_att_locat_out,
                                    img: domain + item.e03_att_img_out,
                                    radius: item.m06_vi_radius,
                                    type: "OUT"
                                });
                            }
                            if (locs.length > 0) setMapData(locs);
                        }}
                    />
                )}

                {/* <LocationOn
                style={{ color: "red", fontSize: "26px", cursor: "pointer", marginTop: "4px" }}
                onClick={() => {
                    const locs = [
                        {
                            lat: 28.7041,
                            lng: 77.1025,
                            address: "IN Location Address (Static)",
                            img: "https://randomuser.me/api/portraits/men/75.jpg",
                            type: "IN"
                        },
                        {
                            lat: 28.5355,
                            lng: 77.3910,
                            address: "OUT Location Address (Static)",
                            img: "https://randomuser.me/api/portraits/women/65.jpg",
                            type: "OUT"
                        }
                    ];
                    setMapData(locs);
                }}
            /> */}
            </div>,
            <div className={rowColor}> <img
                src={domain + item.e03_att_img_out}
                alt="not found"
                className="h-5 w-5 cursor-pointer rounded-full"
                onClick={() => setZoomImg(domain + item.e03_att_img_out)}
            /></div>,
            <div className={rowColor}>{item.e03_att_type_in || "--"}</div>,
            <div className={rowColor}>{item.e03_att_type_out || "--"}</div>,
            <div className={rowColor}>{item.e03_final_status || "--"}</div>,
        ];
    });

    return (
        <div className="mx-5 ">
            <div className="flex justify-start items-center gap-3 p-1 mb-4">
                <p className="font-bold text-xl"> Attendance :</p>
                <div className="flex gap-2  items-end">
                    <div className="p-1 text-sm bg-[#D1FAE5] rounded ">P : {summaryCounts.P}</div>
                    <div className="p-1 text-sm bg-[#FEF3C7] rounded ">HD : {summaryCounts.HD}</div>
                    <div className="p-1 text-sm bg-[#DBEAFE] rounded ">L : {summaryCounts.L}</div>
                    <div className="p-1 text-sm bg-[#FEE2E2] rounded ">A : {summaryCounts.A}</div>
                    <div className="p-1 text-sm bg-[#E9D5FF] rounded ">H : {summaryCounts.H}</div>
                    <div className="p-1 text-sm bg-[#E5E7EB] rounded ">WO : {summaryCounts.WO}</div>
                    <div className="p-1 text-sm bg-[#FED7AA] rounded ">M : {summaryCounts.M}</div>
                </div>
                <div className="flex gap-2 items-end flex-wrap">

                    <select name="status" value={filters.status} onChange={handleFilterChange}
                        className="border px-2 py-1 rounded"> <option value="">Select Status</option>
                        {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <select name="zone" value={filters.zone} onChange={handleFilterChange} className="border  py-1 rounded">
                        <option value="">Select Block</option>
                        {zoneOptions.map(z => (
                            <option key={z.m05_zn_id} value={z.m05_zn_id}>{z.m05_zn_name}</option>
                        ))}
                    </select>

                    <select name="village" value={filters.village} onChange={handleFilterChange} className="border  py-1 rounded">
                        <option value="">Select Villages</option>
                        {villageOptions.map(v => (
                            <option key={v.m06_vi_id} value={v.m06_vi_id}>{v.m06_vi_name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <CustomFilter
                formik={{
                    values: filters,
                    handleChange: handleFilterChange,
                }}
                onFilter={applyFilters}
                onClear={clearFilters}
                onExport={handleExport}
            />

            <TableContainer component={Paper} className="mt-5 max-h-[70vh] overflow-auto">
                <Table stickyHeader>
                    <TableHead className="!bg-[#EBE9FD]">
                        <TableRow>
                            {tableHead.map((head, i) => (
                                <TableCell
                                    key={i}
                                    className="!font-bold !text-center !bg-[#EBE9FD]"
                                >
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {loading ? (
                            Array.from({ length: 10 }).map((_, idx) => (
                                <TableRow key={idx}>
                                    {tableHead.map((_, i) => (
                                        <TableCell key={i}>
                                            <Skeleton />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : attendanceList?.data?.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={tableHead.length} className="text-center py-6 text-gray-600 font-semibold">
                                    No records found
                                </TableCell>
                            </TableRow>
                        ) : (
                            attendanceList.data.map((item, idx) => {
                                const bgColor = statusColors[item.e03_final_status] || "white";
                                return (
                                    <TableRow key={idx} sx={{ backgroundColor: bgColor }}>
                                        <TableCell align="center">{idx + 1}</TableCell>
                                        <TableCell align="center">{item.m05_zn_name || "--"}</TableCell>
                                        <TableCell align="center">{item.m06_vi_name || "--"}</TableCell>
                                        <TableCell align="center">{item.e02_f_name || "--"}</TableCell>
                                        <TableCell align="center">{item.e01_mob || "--"}</TableCell>
                                        <TableCell align="center">
                                            {item.e03_att_datetime_in ? moment.utc(item.e03_att_datetime_in).format("HH:mm:ss DD-MM-YYYY") : "--"}
                                        </TableCell>
                                        <TableCell align="center">
                                            {item.e03_att_datetime_out ? moment.utc(item.e03_att_datetime_out).format("HH:mm:ss DD-MM-YYYY") : "--"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <div className="flex justify-center items-center gap-1">
                                                {item.e03_att_lat_in && (
                                                    <LocationOn
                                                        style={{ color: "red", cursor: "pointer" }}
                                                        onClick={() => {
                                                            const locs = [];
                                                            locs.push({
                                                                lat: Number(item.e03_att_lat_in),
                                                                lng: Number(item.e03_att_lng_in),
                                                                address: item.e03_att_locat_in,
                                                                img: domain + item.e03_att_img_in,
                                                                radius: item.m06_vi_radius,
                                                                type: "IN",
                                                            });
                                                            if (item.e03_att_lat_out && item.e03_att_lat_out !== "0.00000") {
                                                                locs.push({
                                                                    lat: Number(item.e03_att_lat_out),
                                                                    lng: Number(item.e03_att_lng_out),
                                                                    address: item.e03_att_locat_out,
                                                                    img: domain + item.e03_att_img_out,
                                                                    radius: item.m06_vi_radius,
                                                                    type: "OUT",
                                                                });
                                                            }
                                                            setMapData(locs);
                                                        }}
                                                    />
                                                )}
                                                <img
                                                    src={domain + item.e03_att_img_in}
                                                    className="h-7 w-7 rounded-full cursor-pointer"
                                                    onClick={() => setZoomImg(domain + item.e03_att_img_in)}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            <img
                                                src={domain + item.e03_att_img_out}
                                                className="h-7 w-7 rounded-full cursor-pointer"
                                                onClick={() => setZoomImg(domain + item.e03_att_img_out)}
                                            />
                                        </TableCell>
                                        <TableCell align="center">{item.e03_att_type_in || "--"}</TableCell>
                                        <TableCell align="center">{item.e03_att_type_out || "--"}</TableCell>
                                        <TableCell align="center" className="font-semibold">
                                            {item.e03_final_status || "--"}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <CustomToPagination setPage={setPage} page={page} data={attendanceList} />

            {/* Zoom Modal */}
            {zoomImg && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 cursor-pointer"
                    onClick={() => setZoomImg(null)}
                >
                    <img src={zoomImg} alt="Zoom" className="max-h-[90%] max-w-[90%] rounded" />
                </div>
            )}
            {mapData && (
                <CustomMap
                    locations={mapData}
                    onClose={() => setMapData(null)}
                />
            )}
        </div>
    );
};

export default Attendance;

